import {
  useCallback, useEffect, useRef, useState,
  type FormEvent, type MouseEvent as ReactMouseEvent,
} from 'react'
import {
  api, ApiError, can, prettyPhone,
  type Contact,
} from '../lib/api'
import { ImportContacts } from './ImportContacts'
import { PhoneInput } from './PhoneInput'
import {
  ColumnMenu, dash, day, useColumns, useDialogGuard, useInertWhile,
  useReturnFocus, type Column,
} from '../lib/sheet'
import {
  AddressFields, BLANK_ADDRESS, addressBody, addressFrom, type Address,
} from './AddressFields'

/** The phone book — section 06.
 *
 *  Three things fill it and they all land here: typed in by hand, captured
 *  when a call arrives, uploaded in bulk. A record entered here is what turns
 *  the caller-ID pop from a bare number into a name.
 *
 *  IT IS CONTACT DETAILS AND NOTHING ELSE. The record dialog showed a caller's
 *  incident history for a while and it is gone, at the customer's request (6
 *  Sep 2026): this is where an organization keeps who people are, not what has
 *  happened to them. History still exists and still matters — it is on
 *  `CallerCard`, which is what opens when somebody actually rings, and that is
 *  the moment a dispatcher needs to know they have called four times this
 *  month. `GET /api/contacts/{id}/history` is unchanged and still serves it.
 *
 *  A SHEET, A COLUMN PICKER, AND A RECORD DIALOG.
 *
 *  ONE ROW PER PERSON, NOT PER NUMBER. `contact_phones` holds as many numbers
 *  as somebody rings from, and a row per number repeats the name down the
 *  page, which is the one thing a dispatcher is scanning for. The primary
 *  takes the column and the rest ride on it as a count.
 *
 *  ORDER IS THE SERVER'S, in the database's own `en_US.utf8` collation, which
 *  files Élie under E rather than after Z. Sorting again in the browser would
 *  be a second ordering to disagree with the first, and the browser only ever
 *  holds the page it was given.
 */

// ---------------------------------------------------------------------------
// The columns
// ---------------------------------------------------------------------------

/** THE CATALOGUE IS THE ONE SOURCE OF COLUMN ORDER, and that is not a detail.
 *  `prefs.readSet` hands back the STORED array, which preserves whatever order
 *  the boxes happened to be ticked in — so rendering straight from it would
 *  turn the picker into a column-REORDER control by accident: untick Email,
 *  tick it again, and it jumps to the end of the table. The stored value is
 *  membership only, and every render walks this list instead.
 *
 *  Nothing here costs a query. The list endpoint's `_FIELDS` already ships
 *  every one of these with every row.
 *
 *  WHAT IS DELIBERATELY ABSENT: `entry_codes` and the bodies of `site_notes`.
 *  Both are attached to the PLACE, not the person, so two contacts at one
 *  address would show the same door code as though each owned it — and a
 *  column renders every code the organization holds on one scrollable screen.
 *  A site note also has to carry its date and author wherever it appears,
 *  which a one-line cell cannot do. */
const CATALOGUE: Column<Contact>[] = [
  {
    key: 'name', header: 'Name', fixed: true, className: 'nm',
    cell: (c) => c.display_name ?? <span className="muted">No name</span>,
  },
  {
    key: 'phone', header: 'Phone', className: 'num',
    cell: (c) => {
      // `phones[0]` IS the primary — the server orders by `is_primary DESC`.
      // Nothing here sorts or normalises a number; `prettyPhone` is display
      // only. A second normaliser in the browser is the phone-book bug in the
      // one place nobody would think to look for it.
      const primary = c.phones[0]
      if (!primary) return <span className="muted">no number</span>
      const extra = c.phones.length - 1
      return (
        <>
          {prettyPhone(primary.phone_e164)}
          {/* The rest, counted rather than listed. The same man rings from his
              mobile, his office and his house; a row showing two of the three
              is a row that makes somebody create a duplicate. */}
          {extra > 0 && (
            <span
              className="more"
              title={`${extra} more number${extra === 1 ? '' : 's'} on this record`}
            >
              +{extra}
            </span>
          )}
        </>
      )
    },
  },
  {
    key: 'type', header: 'Type',
    cell: (c) => (c.phones[0]?.label
      ? <span className="lbl">{c.phones[0].label}</span>
      : <span className="muted">—</span>),
  },
  { key: 'address', header: 'Address', className: 'addr', cell: (c) => dash(c.address) },
  { key: 'city', header: 'City', cell: (c) => dash(c.city) },
  { key: 'postal_code', header: 'Postal code', className: 'num', cell: (c) => dash(c.postal_code) },
  { key: 'email', header: 'Email', className: 'clip', cell: (c) => dash(c.email) },
  { key: 'language', header: 'Language', cell: (c) => dash(c.language) },
  {
    // Clamped hard, with the whole thing in the tooltip. `notes` runs to 4000
    // characters and a cell that wraps is the dense sheet's whole failure.
    key: 'notes', header: 'Notes', className: 'clip',
    cell: (c) => (c.notes
      ? <span title={c.notes}>{c.notes}</span>
      : <span className="muted">—</span>),
  },
  {
    key: 'numbers', header: 'Numbers', className: 'num',
    cell: (c) => String(c.phones.length),
  },
  { key: 'created_at', header: 'Added', className: 'when', cell: (c) => day(c.created_at) },
  { key: 'updated_at', header: 'Updated', className: 'when', cell: (c) => day(c.updated_at) },
  {
    // Where the record came from. A name the system caught off a ringing phone
    // is not as trustworthy as one a secretary typed, and `contacts.source`
    // already keeps the two apart — so the sheet shows it rather than
    // flattening them into one list.
    //
    // A COLUMN, NOT A BADGE IN THE ACTIONS CELL. It used to ride in `.rowacts`,
    // which made it the one thing on the row nobody could switch off, sitting
    // in the one cell whose contents used to change the row's height.
    // THREE ARMS, NOT TWO. This rendered anything that was not 'call' as
    // "typed" — the most trustworthy label in the column, on the least
    // trustworthy record, the moment a bulk import writes two thousand rows
    // with `source='import'`. Migration 004 created the column precisely to
    // keep the three apart.
    key: 'source', header: 'Source',
    cell: (c) => {
      if (c.source === 'call') {
        return (
          <span
            className="src"
            title="Captured when this number rang in. Nobody has confirmed the name."
          >
            from a call
          </span>
        )
      }
      if (c.source === 'import') {
        return (
          <span
            className="src"
            title="Came in on an uploaded file. Nobody has checked it one by one."
          >
            imported
          </span>
        )
      }
      return <span className="muted">typed</span>
    },
  },
]

/** Exactly what the sheet showed before there was a picker, so nobody's screen
 *  changes under them on the deploy that introduces one. */
const DEFAULT_COLUMNS = ['phone', 'type', 'address', 'language', 'created_at', 'source']
/** UNIQUE PER SHEET. See `useColumns` — two sheets sharing this string read
 *  and then overwrite each other's column choice, silently and one way. */
const COLUMN_PREF = 'phonebook.columns'

/** The server caps at 500 and defaults to 100. Ask for the maximum: the point
 *  of a sheet is scrolling to the end of it. */
const PAGE = 500

const BLANK = {
  display_name: '',
  phone: '',
  alt_phone: '',
  email: '',
  language: '',
  notes: '',
}

// ---------------------------------------------------------------------------

export function PhoneBook() {
  const [rows, setRows] = useState<Contact[]>([])
  const [loaded, setLoaded] = useState(false)
  const [q, setQ] = useState('')
  const [adding, setAdding] = useState(false)
  const [importing, setImporting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [note, setNote] = useState<string | null>(null)
  /** The open record is the CONTACT, never its id. An id would have to be
   *  looked up in `rows`, and the record handed back by a duplicate add is not
   *  in `rows` at all. */
  const [record, setRecord] = useState<Contact | null>(null)
  /** Something the dialog has to say on the way in — "that number was already
   *  somebody's". It cannot go in the page-level notice: the handler that
   *  writes it also opens the dialog, so the notice lands underneath the
   *  backdrop and is read by nobody. */
  const [recordNote, setRecordNote] = useState<string | null>(null)

  const { shown, visible, choose } = useColumns(
    COLUMN_PREF, CATALOGUE, DEFAULT_COLUMNS)

  /** THREE SEPARATE KEYS, AND THIS SCREEN NEEDS ALL THREE BECAUSE THE TAB NO
   *  LONGER MEANS WHAT IT USED TO. Settings admits the Phone book tab on
   *  `phonebook.view` OR `phonebook.import` — the only import control there
   *  has ever been lives here — so a role holding Import and not View now
   *  lands on this screen legitimately and must not be shown a list it cannot
   *  fetch. Add and Edit are their own leaves again: every write below answers
   *  403 without them, and a 403 arriving after somebody has typed a record is
   *  the failure `can()` exists to prevent. */
  const mayView = can('phonebook.view')
  const mayAdd = can('phonebook.add')
  const mayEdit = can('phonebook.edit')

  // Two searches can be in flight at once — the debounce only spaces them out,
  // it does not cancel one. Without a generation guard the SLOWER request wins
  // by landing last, and the sheet ends up showing results for a term the box
  // no longer contains.
  const seq = useRef(0)
  const load = useCallback(async (term: string) => {
    // NOT EVEN ATTEMPTED WITHOUT View, and the guard belongs here rather than
    // at the one call site that runs on a timer: `absorb`, the import and both
    // Remove paths all reload through this, so an import finishing on a role
    // that cannot browse would otherwise fetch a 403 and paint the failure
    // over the report it just produced.
    if (!mayView) return
    const mine = ++seq.current
    try {
      const found = await api.contacts(term, PAGE)
      if (mine !== seq.current) return
      setRows(found)
      setError(null)
    } catch (e) {
      if (mine !== seq.current) return
      setError(e instanceof ApiError ? e.message : 'Could not load the phone book.')
    } finally {
      if (mine === seq.current) setLoaded(true)
    }
  }, [mayView])

  // Typing settles before the search runs. The dispatcher is often mid-call
  // when he types here, and a request per keystroke is noise on the wire for
  // an answer nobody is reading yet.
  useEffect(() => {
    const t = setTimeout(() => load(q), 200)
    return () => clearTimeout(t)
  }, [q, load])

  /** A record changed under the dialog — saved, or a number added or removed.
   *  The dialog stays open on the fresh copy and the row underneath is patched
   *  in place rather than re-running the search: the server already handed
   *  back the whole updated contact. */
  function absorb(updated: Contact | undefined) {
    // A server that answered 204 — an older one, to a tab that was already open
    // across the deploy that changed it — comes through here as undefined.
    // Reload rather than throwing inside a state updater.
    if (!updated) { load(q); return }
    setRecord(updated)
    setRows((prev) => prev.map((r) => (r.id === updated.id ? updated : r)))
  }

  const capped = rows.length >= PAGE
  // The import dialog joins this, or Tab walks out of it into the rows behind
  // and Enter there opens a record over the top of a running import.
  const dialogOpen = adding || importing || record !== null

  const main = useRef<HTMLDivElement>(null)
  useInertWhile(main, dialogOpen)

  return (
    <div className="sheetpage">
      {/* Both notices dismiss on click. They outlive what they are about —
          without that they sit over the sheet through every later search. */}
      {error && (
        <div className="notice" role="alert" onClick={() => setError(null)}>{error}</div>
      )}
      {note && (
        <div className="notice ok" role="status" onClick={() => setNote(null)}>{note}</div>
      )}

      <div className="sheet-main" ref={main}>
      <div className="sheetbar">
        <h2>Phone book</h2>
        <span className="count">{rows.length}{capped ? '+' : ''}</span>
        {/* TWO KEYS, BECAUSE TYPING IN IT IS A DIFFERENT ROUTE FROM OPENING
            it. An empty box lists the book (`phonebook.view`); the moment a
            character is typed it becomes `POST /api/contacts/search`, which is
            `search.callers`. Without View, `load` returns before issuing
            anything, so the box would do literally nothing; without
            search.callers the first keystroke paints a red refusal over a
            sheet that was working a moment earlier. */}
        {mayView && can('search.callers') && (
          <input
            className="search"
            value={q}
            placeholder="Name, number, or street…"
            aria-label="Filter the phone book"
            onChange={(e) => setQ(e.target.value)}
          />
        )}
        <span className="spacer" />
        <ColumnMenu
          catalogue={CATALOGUE}
          visible={visible}
          fallback={DEFAULT_COLUMNS}
          resetLabel="Back to the usual six"
          onChange={choose}
        />
        {/* Most dispatchers hold neither of these: `phonebook.import` is
            deliberately not in the Dispatcher role, and `phonebook.view` came
            off it too — migration 041, not 040. 040 is the one that matched
            nothing and reported success, and repeating its number here would
            re-establish the claim two migration headers and a commit exist to
            correct. What that leaves is a role reaching this tab for the
            importer alone, and a button that answers 403 every time is worse
            than no button. */}
        {can('phonebook.import') && (
          <button
            className="ghost tiny nogrow"
            type="button"
            onClick={() => setImporting(true)}
          >
            Import a file
          </button>
        )}
        {/* GATED NOW, and it was not for months: `POST /api/contacts` has
            always asked for `phonebook.add`, so this drew for every role and
            paid out a 403 to anyone without it — after they had typed a whole
            caller into the dialog. Intake is untouched by this: a dispatcher
            creates a record from the CALLER CARD when somebody rings, which is
            the same leaf and a different screen. */}
        {mayAdd && (
          <button className="primary compact" onClick={() => setAdding(true)}>
            + Add contact
          </button>
        )}
      </div>

      <div className="sheetwrap">
        <table className="sheet">
          <thead>
            <tr>
              {shown.map((c) => <th key={c.key}>{c.header}</th>)}
              <th aria-label="Actions" />
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr>
                {/* colSpan tracks the visible columns. It was a constant, which
                    stops being possible the moment the count is a preference. */}
                <td colSpan={shown.length + 1} className="sheetempty">
                  {!mayView
                    // NOT "empty", and not a spinner either. This role reached
                    // the tab through `phonebook.import`, so the list was never
                    // requested — saying nothing at all here would leave
                    // somebody staring at a blank sheet deciding the phone book
                    // has been wiped.
                    ? 'The list is not yours to browse. This screen is here for the importer above — upload a file and its report will say what it did.'
                    : !loaded
                      // Without this the alarming empty state flashed on every
                      // visit: the 200ms debounce delays the FIRST request too.
                      ? 'Loading…'
                      : q
                        ? 'Nobody matches that. Spelling does not have to be exact — the last four digits of a number is enough.'
                        : 'Empty. Every call will show as an unknown number until somebody is in here.'}
                </td>
              </tr>
            ) : (
              rows.map((c) => (
                <Row
                  key={c.id}
                  contact={c}
                  columns={shown}
                  mayEdit={mayEdit}
                  onOpen={() => setRecord(c)}
                  onRemoved={() => load(q)}
                  onError={setError}
                />
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="sheetfoot">
        <span>
          {rows.length} contact{rows.length === 1 ? '' : 's'}
          {q ? ' matching' : ''}
          {/* Never present a capped list as a complete one. */}
          {capped && ` — the first ${PAGE}. Search to narrow it.`}
        </span>
        <span className="spacer" />
        <span>
          {/* With a term the server orders by similarity, not by name, so the
              claim has to stop being made. */}
          {q ? 'Closest matches first.' : 'Sorted by name.'} Click a row to open
          the record.
        </span>
      </div>
      </div>

      {adding && (
        <AddDialog
          onClose={() => setAdding(false)}
          onError={setError}
          onAdded={async (message, existing) => {
            setAdding(false)
            if (existing) {
              // Open the record we were handed, and carry the message INTO it.
              // It may not be in `rows` at all, which is exactly why the dialog
              // takes a Contact and not an id.
              setRecord(existing)
              setRecordNote(message)
            } else {
              setNote(message)
            }
            await load(q)
          }}
        />
      )}

      {importing && (
        <ImportContacts
          onClose={() => setImporting(false)}
          onImported={(message) => { setNote(message); load(q) }}
        />
      )}

      {record && (
        <RecordDialog
          key={record.id}
          contact={record}
          mayEdit={mayEdit}
          banner={recordNote}
          onDismissBanner={() => setRecordNote(null)}
          onClose={() => { setRecord(null); setRecordNote(null) }}
          onChanged={absorb}
          // A move takes a number OFF somebody else, so their row in the sheet
          // is now wrong — it still shows a number they no longer have, and for
          // a moment one number reads as belonging to two people.
          onMoved={() => load(q)}
          onSaved={(updated) => { absorb(updated); setRecord(null); setRecordNote(null) }}
          onRemoved={() => { setRecord(null); setRecordNote(null); load(q) }}
        />
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// A row
// ---------------------------------------------------------------------------

function Row({
  contact, columns, mayEdit, onOpen, onRemoved, onError,
}: {
  contact: Contact
  columns: Column<Contact>[]
  mayEdit: boolean
  onOpen: () => void
  onRemoved: () => void
  onError: (message: string) => void
}) {
  async function remove(e: ReactMouseEvent) {
    e.stopPropagation()
    if (!confirm(`Remove ${contact.display_name ?? 'this contact'} from the phone book?`)) return
    try {
      await api.contactDelete(contact.id)
      onRemoved()
    } catch (err) {
      onError(err instanceof ApiError ? err.message : 'Could not remove that caller.')
    }
  }

  return (
    // FOCUSABLE, AND THAT IS WHAT MAKES THE ROW BUTTONS REACHABLE AT ALL.
    // Open and Remove are hidden until the row is hovered, and `visibility:
    // hidden` takes them out of the tab order — so with nothing else in the row
    // able to hold focus there was no keyboard route to a record at all. Tab
    // walks the sheet a row at a time; focusing a row reveals its buttons,
    // which puts them in the tab order behind it.
    <tr
      tabIndex={0}
      // A click that ended a text selection is somebody copying a number, not
      // somebody opening a record. Without this, highlighting a number to read
      // it back over the phone throws a dialog over the sheet.
      onClick={() => { if (!window.getSelection()?.toString()) onOpen() }}
      onKeyDown={(e) => {
        if (e.key !== 'Enter' && e.key !== ' ') return
        // Space would otherwise scroll the sheet out from under the row that
        // is about to open.
        e.preventDefault()
        onOpen()
      }}
    >
      {columns.map((c) => (
        <td key={c.key} className={c.className}>{c.cell(contact)}</td>
      ))}
      <td className="rowacts">
        <button
          className="ghost tiny"
          onClick={(e) => { e.stopPropagation(); onOpen() }}
        >
          Open
        </button>
        {/* HIDDEN RATHER THAN DISABLED, unlike the record dialog below. The
            reason a record still draws read-only is that a record is worth
            reading; a Remove button that cannot remove is worth nothing, and
            it would sit at the end of every row on the sheet saying so. */}
        {mayEdit && (
          <button className="ghost tiny" onClick={remove}>Remove</button>
        )}
      </td>
    </tr>
  )
}

// ---------------------------------------------------------------------------
// The record, as a dialog
// ---------------------------------------------------------------------------

/** One contact, on top of everything, and visibly an editing surface.
 *
 *  It used to be a second table row underneath the one you clicked, which put
 *  the record inside the thing it was about and pushed every row below it down
 *  the page.
 *
 *  FOUR WAYS OUT AND ONE GUARD BEHIND ALL OF THEM. The X, Escape, the backdrop
 *  and Cancel all go through `leave()`, so exactly one place decides what
 *  happens to unsaved work. Four doors of which three silently drop your
 *  changes is worse than one door.
 */
function RecordDialog({
  contact, mayEdit, banner, onDismissBanner, onClose, onChanged, onMoved, onSaved,
  onRemoved,
}: {
  contact: Contact
  /** READ-ONLY, NOT ABSENT, for a role without `phonebook.edit`. Opening a
   *  record is how somebody finds out which number to ring back and which door
   *  the buzzer is at, and none of that is an edit. What goes away is every
   *  control that writes — the four routes behind them all answer 403. */
  mayEdit: boolean
  banner: string | null
  onDismissBanner: () => void
  onClose: () => void
  onChanged: (updated: Contact | undefined) => void
  onMoved: () => void
  onSaved: (updated: Contact) => void
  onRemoved: () => void
}) {
  const [form, setForm] = useState({
    display_name: contact.display_name ?? '',
    email: contact.email ?? '',
    language: contact.language ?? '',
    notes: contact.notes ?? '',
  })
  const [address, setAddress] = useState<Address>(addressFrom(contact))
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [newPhone, setNewPhone] = useState('')
  const [newLabel, setNewLabel] = useState('')
  /** A refusal, and THE NUMBER IT WAS ABOUT. Holding only the message meant the
   *  confirm button re-read whatever was in the input at the moment it was
   *  pressed — so editing the field after the refusal and then confirming moved
   *  a DIFFERENT number, deleting it from a third person who was never named on
   *  screen. Clearing the field made the button do nothing at all, silently. */
  const [heldBy, setHeldBy] = useState<{ who: string; phone: string } | null>(null)

  // Seeded once, and that is correct: the only thing that changes `contact`
  // without remounting this component is a phone add or remove, and neither
  // touches these fields.
  const initial = useRef({ form, address })
  const fieldsDirty = JSON.stringify(form) !== JSON.stringify(initial.current.form)
  /** Tracked separately, because it decides whether the address is SENT AT ALL.
   *  The PATCH route treats the presence of any address part as an instruction
   *  to rewrite the whole address, and `addressBody` never emits the free-text
   *  label — so saving a change to somebody's LANGUAGE on a contact whose place
   *  has a label and no structured parts would resolve to no place at all and
   *  write `place_id = NULL`. An address nobody touched is not sent. */
  const addressDirty = JSON.stringify(address) !== JSON.stringify(initial.current.address)
  const dirty = fieldsDirty || addressDirty

  const name = contact.display_name ?? 'this contact'

  /** What would be thrown away by closing now. A number typed into the add box
   *  but never added is not part of `dirty` — it is not a field on the record —
   *  and it was being discarded by every exit without a word. */
  function pendingWork(): string[] {
    const out: string[] = []
    if (dirty) out.push('unsaved changes')
    if (newPhone.trim()) out.push('a number you have not added yet')
    return out
  }

  useReturnFocus()

  // X, Escape, the backdrop and Cancel, all through one guard. The message is
  // built at the moment of leaving so it sees the current state.
  const { leave, backdrop } = useDialogGuard(() => {
    const pending = pendingWork()
    if (!pending.length) return null
    return `${name} has ${pending.join(' and ')}. `
      + `Close anyway and lose ${pending.length > 1 ? 'them' : 'it'}?`
  }, onClose)

  async function save(e: FormEvent) {
    e.preventDefault()
    if (newPhone.trim()
        && !confirm(
          `The number you typed has not been added to ${name}. Save the rest and lose it?`)) {
      return
    }
    setBusy(true)
    setError(null)
    try {
      const updated = await api.contactUpdate(contact.id, {
        display_name: form.display_name.trim(),
        // THE ADDRESS GOES ONLY IF IT CHANGED, and then ALL EIGHT PARTS OF IT.
        // The route reads the presence of any part as "rewrite the address", so
        // a partial set blanks the rest — and `addressBody` cannot emit the
        // free-text label, so sending parts for a place that only has a label
        // resolves to no place and nulls the address of somebody whose language
        // was the only thing edited.
        ...(addressDirty ? addressBody(address) : {}),
        email: form.email.trim() || null,
        language: form.language.trim() || null,
        notes: form.notes.trim() || null,
      })
      onSaved(updated)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not save that change.')
      setBusy(false)
    }
  }

  /** `phone` is passed in rather than read from state, so the confirm button on
   *  a refusal sends the number the refusal was ABOUT. */
  async function addPhone(move: boolean, phone = newPhone.trim()) {
    if (!phone) return
    setBusy(true)
    setError(null)
    try {
      const updated = await api.contactAddPhone(contact.id, {
        phone,
        label: newLabel.trim() || null,
        // Not primary. The number they are already known by is the one that
        // should stay on the row.
        make_primary: false,
        move,
      })
      setNewPhone('')
      setNewLabel('')
      setHeldBy(null)
      onChanged(updated)
      // Somebody else just lost this number, so their row in the sheet is now
      // wrong. `onChanged` only patches the contact in front of us.
      if (move) onMoved()
    } catch (err) {
      if (err instanceof ApiError && err.status === 409) {
        // Held by somebody else. Name them and let the operator decide — a
        // number changing hands silently is how a caller's history quietly
        // leaves his record. Never resend with `move` automatically.
        setHeldBy({ who: err.message, phone })
      } else {
        setError(err instanceof ApiError ? err.message : 'Could not add that number.')
      }
    } finally {
      setBusy(false)
    }
  }

  async function removePhone(id: string, shown: string) {
    // Confirmed, like the two other Remove controls on this screen. It commits
    // immediately, Cancel cannot undo it, and it is one click away from the
    // Remove button on the row above it.
    if (!confirm(`Remove ${shown} from ${name}?`)) return
    setBusy(true)
    setError(null)
    try {
      onChanged(await api.contactRemovePhone(contact.id, id))
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not remove that number.')
    } finally {
      setBusy(false)
    }
  }

  async function remove() {
    if (!confirm(`Remove ${name} from the phone book?`)) return
    setBusy(true)
    try {
      await api.contactDelete(contact.id)
      onRemoved()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not remove that caller.')
      setBusy(false)
    }
  }

  return (
    <div className="modalback" {...backdrop}>
      <form
        className="modal wide sheet-rec"
        role="dialog"
        aria-modal="true"
        aria-labelledby="recname"
        onSubmit={save}
      >
        <header className="sheet-rechead">
          <h3 id="recname">{contact.display_name ?? 'No name'}</h3>
          {/* The difference between looking at a record and having changed it,
              said rather than left to be inferred from the Save button. */}
          <span className={dirty ? 'sheet-recstate dirty' : 'sheet-recstate'}>
            {!mayEdit ? 'Read only' : dirty ? 'Unsaved changes' : 'Editing'}
          </span>
          <span className="spacer" />
          <button className="sheet-recx" type="button" aria-label="Close" onClick={leave}>
            ×
          </button>
        </header>

        <div className="sheet-recbody">
          {/* Inside the scrolling body, not above it: a notice sitting between
              the header and the body renders full-bleed against a container
              that has no padding of its own. */}
          {banner && (
            <div className="notice ok" role="status" onClick={onDismissBanner}>
              {banner}
            </div>
          )}
          {error && <div className="notice" role="alert">{error}</div>}
          {/* SAID, not left to be inferred from a form that quietly refuses to
              take a keystroke. Somebody who cannot tell "this box is locked"
              from "this screen is broken" rings whoever set the system up. */}
          {!mayEdit && (
            <div className="notice wait" role="status">
              You can read this record but not change it — editing the phone
              book is a permission your role does not hold.
            </div>
          )}

          {/* One fieldset over everything that writes, rather than `disabled`
              on each control: a field added below inherits the lock instead of
              being the one that forgot it. */}
          <fieldset className="bare" disabled={!mayEdit}>
          <div className="grid2">
            <div className="field">
              <label htmlFor="rec-name">Name</label>
              <input
                id="rec-name"
                autoFocus
                value={form.display_name}
                onChange={(e) => setForm({ ...form, display_name: e.target.value })}
                required
              />
            </div>
            <div className="field">
              <label htmlFor="rec-email">Email</label>
              <input
                id="rec-email"
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
              />
            </div>
            <div className="field">
              <label htmlFor="rec-lang">Language</label>
              <input
                id="rec-lang"
                value={form.language}
                onChange={(e) => setForm({ ...form, language: e.target.value })}
              />
            </div>
          </div>

          <div className="field">
            <label>Where they live</label>
            <AddressFields value={address} onChange={setAddress} />
          </div>

          <div className="field">
            <label htmlFor="rec-notes">Notes</label>
            <textarea
              id="rec-notes"
              rows={2}
              value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
            />
          </div>

          <h4 className="sheet-recsub">Numbers</h4>
          {/* SAID OUT LOUD, because it is not what a Save button implies: each
              add and each remove is its own committed transaction, and adding a
              number also files that number's past calls under this person.
              Cancel cannot put either back. */}
          {/* PRECISE ABOUT WHICH CALLS. `_add_number` re-files only rows whose
              `contact_id IS NULL` — calls already filed to somebody are left
              alone, deliberately, so attaching a number cannot quietly take
              another person's history. Saying "its past calls" without that
              qualifier promises a repair the server does not perform. */}
          <p className="footnote">
            Numbers save the moment you add or remove one — Cancel does not undo
            them. Adding a number also files any of that number’s past calls
            that are not already attached to somebody under this person.
          </p>
          {contact.phones.length === 0 ? (
            <div className="dim">None yet.</div>
          ) : (
            contact.phones.map((ph) => (
              <div className="listrow sub" key={ph.id}>
                <span className="mono">{prettyPhone(ph.phone_e164)}</span>
                {ph.label && <span className="tag">{ph.label}</span>}
                {ph.is_primary && <span className="tag">main</span>}
                {ph.source === 'call' && <span className="dim">seen on a call</span>}
                <span className="spacer" />
                <button
                  className="ghost tiny"
                  type="button"
                  disabled={busy}
                  onClick={() => removePhone(ph.id, prettyPhone(ph.phone_e164))}
                >
                  Remove
                </button>
              </div>
            ))
          )}

          {heldBy && (
            <div className="notice wait">
              {heldBy.who} Moving {prettyPhone(heldBy.phone)} here takes the
              NUMBER off their record. The calls already filed under their name
              stay with them — this does not move history, so their record and
              this one will each hold part of it.
              <div className="modalrow">
                <button className="ghost" type="button" onClick={() => setHeldBy(null)}>
                  Leave it there
                </button>
                <button
                  className="ghost danger"
                  type="button"
                  disabled={busy}
                  // The number the REFUSAL was about, not whatever is in the
                  // box now.
                  onClick={() => addPhone(true, heldBy.phone)}
                >
                  Move it to {name}
                </button>
              </div>
            </div>
          )}

          {/* Enter in EITHER box adds the number. These inputs sit inside the
              record's own <form>, so without this Enter submitted it — saving
              the contact, closing the dialog, and leaving the number the
              dispatcher had just typed in a box that no longer exists. */}
          <div className="callbar" onKeyDown={(e) => {
            if (e.key !== 'Enter') return
            e.preventDefault()
            addPhone(false)
          }}>
            <input
              className="grow"
              value={newPhone}
              placeholder="Another number"
              // A refusal names one number. The moment the box holds a
              // different one the warning is about nothing on screen.
              onChange={(e) => { setNewPhone(e.target.value); setHeldBy(null) }}
            />
            <input
              value={newLabel}
              placeholder="mobile, office, home"
              onChange={(e) => setNewLabel(e.target.value)}
            />
            <button
              className="ghost tiny nogrow"
              type="button"
              disabled={busy || !newPhone.trim()}
              onClick={() => addPhone(false)}
            >
              Add
            </button>
          </div>
          </fieldset>

        </div>

        <div className="modalrow">
          {/* Both of these WRITE, so they go rather than grey out — the row at
              the foot of a dialog is where somebody looks for the way out, and
              a dead Remove and a dead Save either side of Cancel is three
              things to read instead of one. */}
          {mayEdit && (
            <button className="ghost danger" type="button" disabled={busy} onClick={remove}>
              Remove contact
            </button>
          )}
          <span className="spacer" />
          {/* Nothing can be discarded when nothing can be typed, and "Cancel"
              over a record somebody only read is an offer to undo work they
              never did. */}
          <button className="ghost" type="button" onClick={leave}>
            {mayEdit ? 'Cancel' : 'Close'}
          </button>
          {mayEdit && (
            <button
              className="primary compact"
              disabled={busy || !dirty || !form.display_name.trim()}
            >
              {busy ? 'Saving…' : 'Save'}
            </button>
          )}
        </div>
      </form>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Adding somebody
// ---------------------------------------------------------------------------

function AddDialog({
  onClose, onAdded, onError,
}: {
  onClose: () => void
  onAdded: (message: string, existing?: Contact) => void
  onError: (message: string) => void
}) {
  const [form, setForm] = useState({ ...BLANK })
  const [address, setAddress] = useState<Address>({ ...BLANK_ADDRESS })
  const [busy, setBusy] = useState(false)

  // THE SAME GUARD AS THE RECORD DIALOG, and this is the one where it matters
  // more: a half-typed caller is more typing than any single edit, and this
  // form had four exits — the X, Escape, the backdrop and Cancel — every one of
  // which threw it away without a word.
  //
  // No snapshot ref is needed: the starting state is two constants.
  const { leave, backdrop } = useDialogGuard(
    () => (
      JSON.stringify(form) !== JSON.stringify(BLANK)
      || JSON.stringify(address) !== JSON.stringify(BLANK_ADDRESS)
        ? 'Discard this contact? Nothing has been saved yet.'
        : null),
    onClose,
  )

  async function add(e: FormEvent) {
    e.preventDefault()
    setBusy(true)
    try {
      const saved = await api.contactCreate({
        display_name: form.display_name.trim(),
        phone: form.phone.trim() || null,
        alt_phone: form.alt_phone.trim() || null,
        ...addressBody(address),
        email: form.email.trim() || null,
        language: form.language.trim() || null,
        notes: form.notes.trim() || null,
      })
      if (saved.already_existed) {
        // Not an error, and deliberately not phrased as one: the number was
        // already somebody's, and that somebody is what got handed back.
        onAdded(
          `That number is already ${saved.display_name ?? 'on file'}. `
          + 'Nothing was added — their record is open.',
          saved,
        )
      } else if (saved.second_number_conflict) {
        // The server refuses a second number belonging to somebody else and
        // NAMES them, saving the contact regardless. Reading `already_existed`
        // and nothing else turned that into a green tick over a record with one
        // number fewer than was typed.
        // The server sends a NAME here, not a sentence — `SELECT c.display_name`.
        // Dropped in raw it read "…but not the second number. Mrs Weiss Add it
        // from…". The 409 on the add-a-number path sends a whole sentence; the
        // two are different shapes and this is the one that needs wrapping.
        onAdded(
          `${saved.display_name} added — but not the second number: it is `
          + `already on ${saved.second_number_conflict}. `
          + 'Add it from this record if it should move.',
          saved,
        )
      } else {
        onAdded(`${saved.display_name} added. Calls from that number will now show the name.`)
      }
    } catch (e) {
      onError(e instanceof ApiError ? e.message : 'Could not save that caller.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="modalback" {...backdrop}>
      {/* `sheet-rec` too, not just `wide`: it is what makes the body scroll
          under a header and an action row that stay put, and `.sheet-rechead`
          is written for a container with no padding of its own. */}
      <form
        className="modal wide sheet-rec"
        role="dialog"
        aria-modal="true"
        aria-labelledby="addtitle"
        onSubmit={add}
      >
        <header className="sheet-rechead">
          <h3 id="addtitle">Add a contact</h3>
          <span className="spacer" />
          <button className="sheet-recx" type="button" aria-label="Close" onClick={leave}>
            ×
          </button>
        </header>
        <div className="sheet-recbody">
        <p className="blurb">
          Only a name is required. A caller entered here is recognised the
          moment they ring: the pop shows the name instead of a bare number,
          and every call they make from then on collects onto one record.
        </p>

        <div className="grid2">
          <div className="field">
            <label htmlFor="add-name">Name</label>
            <input
              id="add-name"
              value={form.display_name}
              autoFocus
              onChange={(e) => setForm({ ...form, display_name: e.target.value })}
              required
            />
          </div>
          <div className="field">
            <label htmlFor="add-phone">Phone — type it any way you like</label>
            <PhoneInput
              id="add-phone"
              value={form.phone}
              placeholder="(514) 555-0148"
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
            />
          </div>
          <div className="field">
            <label htmlFor="add-alt">Second number</label>
            <PhoneInput
              id="add-alt"
              value={form.alt_phone}
              onChange={(e) => setForm({ ...form, alt_phone: e.target.value })}
            />
          </div>
          <div className="field">
            <label htmlFor="add-email">Email</label>
            <input
              id="add-email"
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
            />
          </div>
          <div className="field">
            <label htmlFor="add-lang">Language</label>
            <input
              id="add-lang"
              value={form.language}
              placeholder="Yiddish"
              onChange={(e) => setForm({ ...form, language: e.target.value })}
            />
          </div>
        </div>

        <div className="field">
          <label>Where they live</label>
          <AddressFields value={address} onChange={setAddress} />
        </div>

        <div className="field">
          <label htmlFor="add-notes">
            Notes — what a dispatcher should know before answering
          </label>
          <textarea
            id="add-notes"
            rows={2}
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
          />
        </div>

        <p className="footnote">
          The address is what a call from this person can fill in at intake —
          but only when the “calling for” answer says the incident is at their
          own address. A passer-by reporting something three streets away never
          brings his own address with him.
        </p>
        </div>

        <div className="modalrow">
          <button className="ghost" type="button" onClick={leave}>Cancel</button>
          <button className="primary compact" disabled={busy || !form.display_name.trim()}>
            {busy ? 'Saving…' : 'Add to the phone book'}
          </button>
        </div>
      </form>
    </div>
  )
}
